import {
  getStatus,
  getIconType,
  getImportance,
  getSolvability,
} from '../../constants/seoMappings';

/**
 * Calculates a human-friendly date string from a timestamp
 * @param {number} timestamp - The expiration timestamp in milliseconds
 * @param {boolean} isExpired - Whether the certificate is expired
 * @returns {string} - Friendly date string like "expires in 4 months" or "expired 2 months ago"
 */
const calculateFriendlyDate = (timestamp, isExpired) => {
  if (!timestamp) {
    return '';
  }

  const now = Date.now();
  const diffMs = timestamp - now;
  const diffDays = Math.abs(Math.round(diffMs / (1000 * 60 * 60 * 24)));

  let timeString = '';

  if (diffDays < 1) {
    timeString = 'less than a day';
  } else if (diffDays === 1) {
    timeString = '1 day';
  } else if (diffDays < 30) {
    timeString = `${diffDays} days`;
  } else if (diffDays < 60) {
    timeString = '1 month';
  } else if (diffDays < 365) {
    const months = Math.round(diffDays / 30);
    timeString = `${months} months`;
  } else if (diffDays < 730) {
    timeString = '1 year';
  } else {
    const years = Math.round(diffDays / 365);
    timeString = `${years} years`;
  }

  if (isExpired) {
    return `expired ${timeString} ago`;
  }
  return `expires in ${timeString}`;
};

/**
 * Generates message for SSL secure section
 * @param {Object} data - The SSL data object
 * @returns {string} - Message for the section
 */
const generateMessage = (data) => {
  if (!data?.hasCertificate) {
    return 'Warning! No SSL certificate was detected.';
  }
  return 'Great, your website is SSL secured (HTTPS).';
};

/**
 * Transforms the ssl_secured criteria from WooRank API response
 * @param {Object} sslSecuredCriteria - The ssl_secured criteria object from the API
 * @returns {Object} - Transformed SSL secure data for React components
 */
export const transformSslSecure = (sslSecuredCriteria) => {
  // Handle missing criteria - use 'neutral' status (maps to 'info' icon)
  if (!sslSecuredCriteria) {
    return {
      status: getStatus('neutral'),
      iconType: getIconType('neutral'),
      importance: { level: 0, text: '' },
      solvability: { level: 0, text: '' },
      isEmpty: true,
      message: 'Warning! No SSL certificate was detected.',
      data: null,
    };
  }

  const data = sslSecuredCriteria?.data || {};
  const wooRankStatus = sslSecuredCriteria?.status || 'neutral';
  const importance = sslSecuredCriteria?.importance || 0;
  const solvability = sslSecuredCriteria?.solvability || 0;

  const hasCertificate = data?.hasCertificate ?? false;
  const httpsRedirect = data?.httpsRedirect ?? false;
  const stsHeader = data?.stsHeader ?? false;
  const certificateExpired = data?.certificateExpired ?? false;
  const certificateExpirationDate = data?.certificateExpirationDate || null;
  const certificateIssuer = data?.certificateIssuer || '';

  const isEmpty = !hasCertificate;

  return {
    status: getStatus(wooRankStatus),
    iconType: getIconType(wooRankStatus),
    importance: getImportance(importance),
    solvability: getSolvability(solvability),
    isEmpty,
    message: generateMessage(data),
    data: {
      hasCertificate,
      httpsRedirect,
      stsHeader,
      certificateExpired,
      certificateExpirationFriendly: calculateFriendlyDate(certificateExpirationDate, certificateExpired),
      certificateIssuer,
    },
  };
};

export default transformSslSecure;

